/* eslint-disable max-lines */
import { FC, useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import {
  FinanceWidgetEvents,
  MiniWidgetViewType,
  MinimalLoader,
  PriceDisplayType,
  Translation,
  Version,
  customEvent,
  usePlatformConfig,
  usePriceFormatter,
  ScrollInViewBehaviourType,
  useScrollToSection,
  useWidgetContext,
  errorHandler,
  financeFormInputStorage,
} from '@dxp-module/common'
import { Box, Button, Typography } from '@mui/material'
import KeyboardArrowDownSharpIcon from '@mui/icons-material/KeyboardArrowDownSharp'
import { useSimulationInfoQuery } from '../../react/useSimulationInfoQuery'
import FinanceCard from '../financeCard/FinanceCard'
import CalculatorIcon from '../icons/CalculatorIcon'
import { isCurrentInputCash } from '../../../common/utils/isCurrentInputCash'
import miniFcWidgetButtonStyles from './miniFcWidgetButton.styles'

export type SimulationInfo = {
  amount: number
  frequency: string
}

export type CarConfigURLInfo = {
  config_url: string[]
}

type MiniFcWidgetButtonProps = {
  decimals?: number
  useCurrencySign?: boolean
  currency?: string
}

// TODO fix react hook linting error exhaustive-deps
/* eslint-disable react-hooks/exhaustive-deps */
const MiniFcWidgetButton: FC<MiniFcWidgetButtonProps> = ({
  decimals = 0,
  useCurrencySign = true,
  currency,
}) => {
  const {
    configuration: {
      car,
      dimensions,
      containerId,
      mode,
      version: fcVersion,
      reffineIntegration,
    },
  } = useWidgetContext()
  const styles = miniFcWidgetButtonStyles()
  const { scrollToSection } = useScrollToSection()
  const isIprVersion = fcVersion === Version.IPR

  const {
    platformConfig: {
      priceDisplay,
      financeCalculator: { useMiniFc },
    },
  } = usePlatformConfig()

  const {
    currency: displayCurrency,
    primary: { decimalScale },
    finance,
  } = priceDisplay
  const { subunitToUnit = 100 } = priceDisplay[PriceDisplayType.PRIMARY]

  const priceFormatter = usePriceFormatter({
    currency: currency || displayCurrency,
    decimals: decimals ? decimals : decimalScale,
    useCurrencySign,
    separator: finance?.thousandSeparator,
    decimalSeparator: finance?.decimalSeparator,
  })

  const [isCash, setIsCash] = useState(isCurrentInputCash(dimensions))
  const [carCode, setCarCode] = useState(car?.code || '')
  const [carPrice, setCarPrice] = useState(
    car?.totalPrice?.primaryCurrencyPrice || ''
  )
  const [fcActive, setFcActive] = useState(false)
  const [simulationData, setSimulationData] = useState<SimulationInfo | null>(
    null
  )
  const [version, setVersion] = useState(1)
  const [containerHeight, setContainerHeight] = useState(0)
  const [amount, setAmount] = useState('0')
  const [hasConfigUrl, setHasConfigUrl] = useState<boolean>(false)

  const { querySimulationInfo, loading } = useSimulationInfoQuery(
    setIsCash,
    setSimulationData
  )

  const resizeHandler = useCallback(() => {
    const container = document.getElementById(containerId)
    if (container) {
      setContainerHeight(container.clientHeight)
    }
  }, [])

  const updateFcActive = useCallback(
    (evt: Event) => {
      const value = (evt as CustomEvent).detail
      setFcActive(value)

      if (reffineIntegration) {
        window.document
          .getElementsByClassName('dxp-fc-container')[0]
          .setAttribute('style', 'scroll-margin-top: 200px')
        scrollToSection(
          '[id="finance-calculator-container"]',
          ScrollInViewBehaviourType.SMOOTH
        )
      }
    },
    [scrollToSection]
  )

  const updateFcData = useCallback((evt: Event) => {
    const simulation = (evt as CustomEvent).detail
    if (simulation?.repayment?.value) {
      setIsCash(false)
      const { frequency, value } = simulation.repayment
      setSimulationData({
        amount: value,
        frequency,
      })
    } else {
      setVersion(v => ++v)
    }
  }, [])

  const updateCarCode = useCallback((evt: Event) => {
    const code = (evt as CustomEvent).detail
    setCarCode(code)
    setVersion(v => ++v)
  }, [])

  const updateCarPrice = useCallback((evt: Event) => {
    const price = (evt as CustomEvent).detail
    setCarPrice(price)
  }, [])

  useEffect(() => {
    setCarCode(car?.code)
  }, [car])

  useEffect(() => {
    if (reffineIntegration) {
      return customEvent.emit(FinanceWidgetEvents.FC_ACTIVE_REFFINE, fcActive)
    }
  }, [fcActive])

  useEffect(() => {
    const container = document.getElementById(containerId)
    if (container) {
      setContainerHeight(container.clientHeight)
    }
    window.addEventListener('resize', resizeHandler)
    customEvent.listen(FinanceWidgetEvents.FC_ACTIVE, updateFcActive)
    if (useMiniFc) {
      customEvent.listen(FinanceWidgetEvents.FC_DATA, updateFcData)
      customEvent.listen(FinanceWidgetEvents.UPDATE_CAR, updateCarCode)
    }
    isIprVersion &&
      customEvent.listen(FinanceWidgetEvents.UPDATE_PRICE, updateCarPrice)

    return () => {
      window.removeEventListener('resize', resizeHandler)
      customEvent.removeListener(FinanceWidgetEvents.FC_ACTIVE, updateFcActive)
      if (useMiniFc) {
        customEvent.removeListener(FinanceWidgetEvents.FC_DATA, updateFcData)
        customEvent.removeListener(
          FinanceWidgetEvents.UPDATE_CAR,
          updateCarCode
        )
      }
      isIprVersion &&
        customEvent.removeListener(
          FinanceWidgetEvents.UPDATE_PRICE,
          updateCarPrice
        )
    }
  }, [updateFcActive, isIprVersion, useMiniFc])

  useEffect(() => {
    if (!isCash) {
      if (simulationData?.amount && !loading) {
        const price = priceFormatter.format(
          simulationData.amount ? simulationData.amount / subunitToUnit : 0
        )
        setAmount(price)
      }
    }
  }, [simulationData, loading, isCash])

  useEffect(() => {
    const isNextCash = isCurrentInputCash(dimensions)
    setIsCash(isNextCash)

    if (isNextCash) {
      setSimulationData(null)
      return
    }

    if (carCode && financeFormInputStorage.get(dimensions)) {
      querySimulationInfo([carCode], financeFormInputStorage.get(dimensions))
    }
  }, [version])

  useEffect(() => {
    if (reffineIntegration) {
      ;(async () => {
        try {
          await window
            .fetch(carCode)
            .then(response => {
              if (!response.ok) {
                throw new Error('Unable to fetch ovsd data')
              }

              return response.json()
            })
            .then(data => {
              const configURL = data?.data
              const { config_url } = configURL?.attributes || {}

              setHasConfigUrl(
                config_url && Object.keys(config_url).length !== 0
              )
            })
        } catch (error) {
          errorHandler.handleError(error)
        }
      })()
    }
  }, [carCode])

  let content = (
    <Typography className={styles.cash} variant="caption">
      {isIprVersion ? (
        <Typography component="span">{carPrice}</Typography>
      ) : (
        <Translation
          className={styles.label}
          id="trans__fincalc__sticky_widget__finance_options_label"
        />
      )}
    </Typography>
  )

  if (!isCash) {
    if (simulationData?.amount && !loading) {
      content = (
        <Box className={styles.repayment} data-test="mini:fc:repayment">
          <Typography variant="body1" className={styles.repaymentAmount}>
            {amount}
          </Typography>
          <Typography variant="caption" className={styles.repaymentFrequency}>
            <Translation
              id={
                isIprVersion
                  ? 'trans__fincalc__sticky_widget__repayment_label'
                  : simulationData.frequency
              }
            />
          </Typography>
        </Box>
      )
    } else {
      content = <MinimalLoader width={79} animated={true} />
    }
  }

  if (reffineIntegration && !hasConfigUrl) {
    return null
  }

  return mode === MiniWidgetViewType.CARD ? (
    <FinanceCard
      amount={amount}
      isCash={isCash}
      loading={loading}
      dimensions={dimensions}
    />
  ) : (
    <Button
      className={clsx({
        [styles.button]: !reffineIntegration,
        [styles.selected]: fcActive && !isIprVersion,
        [styles.reffineButton]: reffineIntegration,
        [styles.iprButton]: isIprVersion,
      })}
      style={{ height: containerHeight }}
      disableRipple={true}
      onClick={() => setFcActive(prev => !prev)}
      variant="text"
      startIcon={
        !isIprVersion && <CalculatorIcon className={styles.calculatorIcon} />
      }
      endIcon={
        !isIprVersion && (
          <KeyboardArrowDownSharpIcon
            className={clsx(styles.arrowIcon, {
              [styles.arrowUp]: fcActive,
            })}
          />
        )
      }
      data-test="mini:fc:button"
    >
      {content}
    </Button>
  )
}

export default MiniFcWidgetButton
